import RootStore from '@App/stores/root-store'
import { PaginationBody, PaginationBodyRightsHolders, PaginationInfo } from '@Entities/pagination'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { AxiosResponse } from 'axios'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { RIGHTSHOLDER_GROUP } from '@Const/rightsholder-statuses'
import { generateRightsholderPaginationStatus } from '@App/helpers/get-rigthsholder-pagination-status'
import { searchRightsholders } from '@App/api/routes/rightsholder/rightsholder'
import { MemberSummary } from '@Entities/member/member-summary'
import { ResultPage } from '@Entities/result-page'

class RightsholderSearchStore {
    private rootStore: RootStore

    private _rightsholders: ResultPage<MemberSummary>

    private _paginationInfo: PaginationInfo

    private _currentSearchValue = ''

    constructor(rootStore: RootStore) {
        makeObservable<RightsholderSearchStore, '_rightsholders' | '_paginationInfo' | '_currentSearchValue'>(this, {
            _rightsholders: observable,
            _paginationInfo: observable,
            _currentSearchValue: observable,
            setCurrentSearchValue: action,
            rightsholderCount: computed,
        })
        this.rootStore = rootStore
    }

    get rightsholders() {
        return this._rightsholders
    }

    get paginationInfo() {
        return this._paginationInfo
    }

    get currentSearchValue() {
        return this._currentSearchValue
    }

    get rightsholderCount(): number {
        if (!this._rightsholders) {
            return 0
        }
        return this._rightsholders.totalElements
    }

    setCurrentSearchValue(value: string) {
        this._currentSearchValue = value
    }

    public loadRightsholders = async (paginationInfo: PaginationBody, search?: string) => {
        this.setCurrentSearchValue(search ?? '')
        await this.setRightsholdersAndPagination(paginationInfo, (passedPagInfo: PaginationBodyRightsHolders) =>
            searchRightsholders(passedPagInfo, search)
        )
    }

    private setRightsholdersAndPagination = async (
        paginationInfo: PaginationBodyRightsHolders,
        fetchFn: (paginationInfo: PaginationBodyRightsHolders) => Promise<AxiosResponse<ResultPage<MemberSummary>>>
    ) => {
        const newPagination = this.processRightsholderPagination(paginationInfo)
        const rightsholders = await fetchFn(paginationInfo)
        runInAction(() => {
            this._rightsholders = rightsholders.data
            if (newPagination.limit && newPagination.page && rightsholders) {
                this._paginationInfo = calcNumPages(
                    rightsholders.data.totalElements,
                    newPagination.limit,
                    newPagination.page
                )
            }
        })
    }

    private processRightsholderPagination = (pagination: PaginationBodyRightsHolders): PaginationBodyRightsHolders => {
        if (pagination.pending) {
            pagination.status = RIGHTSHOLDER_GROUP.pending
        }
        const newStatus = generateRightsholderPaginationStatus(pagination.status)
        const newPagination = pagination
        newStatus ? (newPagination.status = newStatus) : delete newPagination.status
        return newPagination
    }
}

export default RightsholderSearchStore
